/** An activity's visit duration as a range of hours + minutes (plan §3 Visit card,
 *  §10.2): `[1 h][30 min] – [2 h][  min]`, stored as decimal hours (`min_duration` /
 *  `max_duration`). A plain controlled component; the record editor's `duration` widget
 *  wraps it (the registry, `index.ts`).
 *
 *  Like the record editor's integer widget, each bound keeps the operator's text while
 *  they type or while it is not a value yet ("1x", 75 minutes): that is reported through
 *  `onInvalid` and shown under the inputs, and never reaches `onChange`. A value that is
 *  well-formed but breaks the range rule (minimum longer than the maximum) does reach
 *  `onChange` and is shown here; the caller blocks its save with the same
 *  `durationRangeError`. `readOnly` is view mode: the range in words
 *  (`formatHoursRange`) on the inputs' height. */
import { Box, InputAdornment, TextField, Typography } from '@mui/material';
import { useEffect, useId, useRef, useState, type FocusEvent } from 'react';

import { formatHoursRange } from '../../time/hours';

import {
  BOUND_LABEL,
  boundText,
  durationRangeError,
  parseBound,
  type BoundPart,
  type BoundText,
  type DurationBound,
  type DurationRangeValue,
} from './durationRangeModel';
import { WidgetCaption } from './WidgetCaption';

export interface DurationRangeProps {
  value: DurationRangeValue;
  onChange: (value: DurationRangeValue) => void;
  /** An input the value cannot hold yet (the record editor's `onInvalid`); null when none. */
  onInvalid?: (message: string | null) => void;
  /** Focus enters / leaves the whole range (one undo step per focus session). */
  onFocus?: () => void;
  onBlur?: () => void;
  disabled?: boolean;
  /** View mode: the range in words. */
  readOnly?: boolean;
  /** An error from outside (the server's), shown under the inputs. */
  error?: string | null;
  /** false: no error caption of its own — its host captions the same messages (the record
   *  editor's FieldSlot, from `onInvalid` and the range rule as a `checkValue`), and the
   *  inputs point at that caption (`describedBy`). They still turn red. Default true. */
  inlineErrors?: boolean;
  /** The group's accessible name when no `labelledBy` is given (default "Duration"). */
  label?: string;
  labelledBy?: string;
  describedBy?: string;
  id?: string;
}

const BOUNDS: readonly DurationBound[] = ['min', 'max'];

type Invalid = Partial<Record<DurationBound, { part: BoundPart; message: string }>>;

function without<T extends object>(o: T, keys: readonly (keyof T)[]): T {
  const next = { ...o };
  for (const k of keys) delete next[k];
  return next;
}

const NUMBER_INPUT_SX = {
  '& input': { fontVariantNumeric: 'tabular-nums', textAlign: 'right' },
  // The theme's 42 % placeholder is under 4.5:1; a placeholder here means a real 0.
  '& input::placeholder': { color: 'text.secondary', opacity: 1 },
} as const;

export function DurationRange({
  value,
  onChange,
  onInvalid,
  onFocus,
  onBlur,
  disabled = false,
  readOnly = false,
  error,
  inlineErrors = true,
  label = 'Duration',
  labelledBy,
  describedBy,
  id,
}: DurationRangeProps) {
  const uid = useId();
  const captionId = `${uid}-caption`;
  const [typed, setTyped] = useState<Partial<Record<DurationBound, BoundText>>>({});
  const [invalid, setInvalid] = useState<Invalid>({});
  const focused = useRef<DurationBound | null>(null);

  // A bound's stored value changed from outside (an undo, a reload) while the operator is
  // not in it: drop what it held, so the new value shows through.
  const last = useRef(value);
  useEffect(() => {
    const prev = last.current;
    last.current = value;
    const stale = BOUNDS.filter((b) => prev[b] !== value[b] && focused.current !== b);
    if (!stale.length) return;
    setTyped((t) => without(t, stale));
    setInvalid((i) => without(i, stale));
  }, [value]);

  const message =
    BOUNDS.map((b) => invalid[b] && `${BOUND_LABEL[b]}: ${invalid[b]!.message}`).find(Boolean) ||
    null;
  const reported = useRef<string | null>(null);
  useEffect(() => {
    if (reported.current === message) return;
    reported.current = message;
    onInvalid?.(message);
  }, [message, onInvalid]);

  const groupName = labelledBy ? { 'aria-labelledby': labelledBy } : { 'aria-label': label };

  if (readOnly) {
    return (
      <Box
        role="group"
        id={id}
        {...groupName}
        sx={{ minHeight: 40, display: 'flex', alignItems: 'center' }}
      >
        <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>
          {formatHoursRange(value.min, value.max)}
        </Typography>
      </Box>
    );
  }

  const shown = (b: DurationBound): BoundText => typed[b] ?? boundText(value[b]);

  const change = (b: DurationBound, part: 'h' | 'm', raw: string) => {
    const next = { ...shown(b), [part]: raw };
    setTyped((t) => ({ ...t, [b]: next }));
    const parsed = parseBound(next);
    if (!parsed.ok) {
      setInvalid((i) => ({ ...i, [b]: { part: parsed.part, message: parsed.message } }));
      return;
    }
    setInvalid((i) => without(i, [b]));
    if (parsed.hours !== value[b]) onChange({ ...value, [b]: parsed.hours });
  };

  const rangeError = message ? null : durationRangeError(value);
  const messages = [message, rangeError, error].filter((m): m is string => !!m);
  const captioned = inlineErrors && messages.length > 0;
  const describedByIds = [captioned ? captionId : null, describedBy].filter(Boolean).join(' ') || undefined;

  const leaves = (e: FocusEvent<HTMLElement>) =>
    !e.currentTarget.contains(e.relatedTarget as Node | null);

  const bound = (b: DurationBound) => {
    const text = shown(b);
    const bad = invalid[b];
    const set = parseBound(text);
    // A placeholder 0 only where a blank input means 0: in a bound that has a value.
    const placeholder = set.ok && set.hours !== null ? '0' : undefined;
    const field = (part: 'h' | 'm') => (
      <TextField
        size="small"
        value={text[part]}
        disabled={disabled}
        error={!!rangeError || (!!bad && (bad.part === part || bad.part === 'both'))}
        onChange={(e) => change(b, part, e.target.value)}
        placeholder={placeholder}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end" sx={{ ml: 0.5 }}>
              {part === 'h' ? 'h' : 'min'}
            </InputAdornment>
          ),
        }}
        inputProps={{
          'aria-label': `${BOUND_LABEL[b]} ${part === 'h' ? 'hours' : 'minutes'}`,
          'aria-describedby': describedByIds,
          inputMode: part === 'h' ? 'decimal' : 'numeric',
          autoComplete: 'off',
          'data-part': `${b}-${part}`,
        }}
        sx={{ width: part === 'h' ? 80 : 88, ...NUMBER_INPUT_SX }}
      />
    );
    return (
      <Box
        role="group"
        aria-label={BOUND_LABEL[b]}
        sx={{ display: 'flex', gap: 0.5 }}
        onFocus={() => {
          focused.current = b;
        }}
        onBlur={(e) => {
          if (!leaves(e)) return;
          focused.current = null;
          // Show the stored value again (1.5 h → 1 h 30 min) unless the text is not one yet.
          if (!invalid[b]) setTyped((t) => without(t, [b]));
        }}
      >
        {field('h')}
        {field('m')}
      </Box>
    );
  };

  return (
    <Box
      role="group"
      id={id}
      {...groupName}
      onFocus={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) onFocus?.();
      }}
      onBlur={(e) => {
        if (leaves(e)) onBlur?.();
      }}
    >
      <Box
        sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 1, rowGap: 0.5 }}
      >
        {bound('min')}
        <Typography aria-hidden variant="body2" color="text.secondary">
          –
        </Typography>
        {bound('max')}
      </Box>
      {captioned && (
        <Box id={captionId} sx={{ mt: 0.5 }}>
          {messages.map((m) => (
            <WidgetCaption key={m} tone="error">
              {m}
            </WidgetCaption>
          ))}
        </Box>
      )}
    </Box>
  );
}
